import { HOLIDAY_IDS, HOLIDAY_LABELS, type HolidayId } from '@/constants/holiday-hours'
import type {
  HolidayHourRule,
  HolidayRuleRow,
  HoursCoverageConfig,
  SpecialHourEntry,
} from '@/types/configuration'
import {
  ActionIcon,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { getHolidayMonthDayLabels } from '@/utils/holiday-display-dates'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { useEffect, useMemo } from 'react'
import { ConfigFormHeader, useConfigViewOnly } from '@/components/ConfigFormHeader'

interface HoursCoverageFormProps {
  data: HoursCoverageConfig | null
  onSave: (data: HoursCoverageConfig) => void
  isSaving: boolean
}

type HoursCoverageFormValues = HoursCoverageConfig & { holidayRuleRows: HolidayRuleRow[] }

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const DEPARTMENT_OPTIONS = [
  { value: 'sales', label: 'Sales' },
  { value: 'service', label: 'Service' },
  { value: 'parts', label: 'Parts' },
  { value: 'finance', label: 'Finance' },
]

const timeInputStyles = { input: { width: 120 } }

function rowsFromRecord(rules: HoursCoverageConfig['holidayHourRules'] | undefined): HolidayRuleRow[] {
  if (!rules) {
    return []
  }
  const rows: HolidayRuleRow[] = []
  for (const [id, r] of Object.entries(rules) as [HolidayId, HolidayHourRule][]) {
    if (!r || (r.mode !== 'closed' && r.mode !== 'shortened')) {
      continue
    }
    rows.push({
      holidayId: id,
      mode: r.mode === 'closed' ? 'closed' : 'shortened',
      openTime: r.openTime,
      closeTime: r.closeTime,
    })
  }
  return rows.sort((a, b) => HOLIDAY_IDS.indexOf(a.holidayId) - HOLIDAY_IDS.indexOf(b.holidayId))
}

function recordFromRows(rows: HolidayRuleRow[]): NonNullable<HoursCoverageConfig['holidayHourRules']> {
  const o: Partial<Record<HolidayId, HolidayHourRule>> = {}
  for (const row of rows) {
    if (row.mode === 'closed') {
      o[row.holidayId] = { mode: 'closed' }
    } else {
      o[row.holidayId] = {
        mode: 'shortened',
        openTime: row.openTime ?? '10:00',
        closeTime: row.closeTime ?? '16:00',
      }
    }
  }
  return o
}

function getDefaultFormValues(): HoursCoverageFormValues {
  return {
    timezone: 'America/Chicago',
    regularHours: {
      sales: {
        department: 'sales',
        days: [1, 2, 3, 4, 5, 6, 0].map(dayOfWeek => ({
          dayOfWeek,
          openTime: '09:00',
          closeTime: '17:00',
          isClosed: false,
        })),
      },
    },
    quietHours: {
      enabled: false,
      startTime: '22:00',
      endTime: '08:00',
    },
    holidayHourRules: {},
    holidayRuleRows: [],
    specialHours: [],
  }
}

function mergeHoursCoverageData(data: Partial<HoursCoverageConfig> | null): HoursCoverageFormValues {
  const defaults = getDefaultFormValues()
  if (!data) {
    return defaults
  }
  return {
    ...defaults,
    ...data,
    regularHours: data.regularHours ?? defaults.regularHours,
    quietHours: { ...defaults.quietHours, ...data.quietHours },
    holidayHourRules: data.holidayHourRules ?? {},
    holidayRuleRows: rowsFromRecord(data.holidayHourRules),
    specialHours: Array.isArray(data.specialHours) ? [...data.specialHours] : [],
  }
}

function buildSavePayload(values: HoursCoverageFormValues): HoursCoverageConfig {
  const { holidayRuleRows, ...rest } = values
  const holidayHourRules = recordFromRows(holidayRuleRows)
  const specialHours = (values.specialHours ?? []).filter(row => row.date?.trim().length > 0)

  return {
    ...rest,
    holidayHourRules: Object.keys(holidayHourRules).length > 0 ? holidayHourRules : {},
    specialHours,
  }
}

function validateHolidayRuleRows(rows: HolidayRuleRow[]): string | null {
  for (const row of rows) {
    if (row.mode === 'shortened' && (!row.openTime?.trim() || !row.closeTime?.trim())) {
      return `Shortened hours for ${HOLIDAY_LABELS[row.holidayId]} require open and close times.`
    }
  }
  return null
}

const emptySpecialHour = (): SpecialHourEntry => ({
  date: '',
  department: 'sales',
  openTime: '09:00',
  closeTime: '17:00',
  isClosed: false,
  description: '',
})

export function HoursCoverageForm({ data, onSave, isSaving }: HoursCoverageFormProps) {
  const viewOnly = useConfigViewOnly()
  const form = useForm<HoursCoverageFormValues>({
    initialValues: getDefaultFormValues(),
  })

  const holidayLabelYear = useMemo(() => new Date().getFullYear(), [])
  const holidayDateLabels = useMemo(() => getHolidayMonthDayLabels(holidayLabelYear), [holidayLabelYear])

  useEffect(() => {
    form.setValues(mergeHoursCoverageData(data))
  }, [data])

  const handleSubmit = (values: HoursCoverageFormValues) => {
    const ids = values.holidayRuleRows.map(r => r.holidayId)
    if (new Set(ids).size !== ids.length) {
      notifications.show({ title: 'Duplicate holiday', message: 'Each holiday can only appear once.', color: 'red' })
      return
    }
    const msg = validateHolidayRuleRows(values.holidayRuleRows)
    if (msg) {
      notifications.show({ title: 'Check holiday hours', message: msg, color: 'red' })
      return
    }
    onSave(buildSavePayload(values))
  }

  const specialHours = form.values.specialHours ?? []
  const holidayRuleRows = form.values.holidayRuleRows

  const addHolidayRow = () => {
    const used = new Set(holidayRuleRows.map(r => r.holidayId))
    const nextId = HOLIDAY_IDS.find(id => !used.has(id))
    if (!nextId) {
      notifications.show({
        title: 'No holidays left',
        message: 'All catalog holidays already have a rule.',
        color: 'yellow',
      })
      return
    }
    form.setFieldValue('holidayRuleRows', [
      ...holidayRuleRows,
      { holidayId: nextId, mode: 'shortened', openTime: '10:00', closeTime: '16:00' },
    ])
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <ConfigFormHeader onReset={() => form.setValues(mergeHoursCoverageData(data))} isSaving={isSaving} />
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={600}>Business hours</Text>
            <Select
              label="Timezone"
              data={[
                { value: 'America/New_York', label: 'Eastern Time' },
                { value: 'America/Chicago', label: 'Central Time' },
                { value: 'America/Denver', label: 'Mountain Time' },
                { value: 'America/Los_Angeles', label: 'Pacific Time' },
              ]}
              {...form.getInputProps('timezone')}
            />

            {form.values.regularHours.sales.days.map((day, index) => {
              const dayName = DAY_NAMES[day.dayOfWeek]
              return (
                <Group key={day.dayOfWeek} justify="space-between" align="center" wrap="nowrap">
                  <Text w={100}>{dayName}</Text>
                  <Group wrap="nowrap" gap="xs">
                    <TextInput
                      type="time"
                      disabled={day.isClosed}
                      {...form.getInputProps(`regularHours.sales.days.${index}.openTime`)}
                      styles={timeInputStyles}
                    />
                    <Text size="sm" c="dimmed">
                      –
                    </Text>
                    <TextInput
                      type="time"
                      disabled={day.isClosed}
                      {...form.getInputProps(`regularHours.sales.days.${index}.closeTime`)}
                      styles={timeInputStyles}
                    />
                    <Switch
                      label={day.isClosed ? 'Closed' : 'Open'}
                      checked={!day.isClosed}
                      onChange={event =>
                        form.setFieldValue(
                          `regularHours.sales.days.${index}.isClosed`,
                          !event.currentTarget.checked,
                        )
                      }
                    />
                  </Group>
                </Group>
              )
            })}

            <Divider my="xs" />

            <Group justify="space-between" align="center" wrap="wrap" gap="sm">
              <Text miw={140} maw={360} fw={600} size="sm" style={{ flex: '1 1 200px' }}>
                Quiet hours
              </Text>
              <Group gap="sm" wrap="wrap" justify="flex-end" align="center" style={{ flex: 1, minWidth: 200 }}>
                <Switch
                  size="sm"
                  label="Enable"
                  checked={form.values.quietHours.enabled}
                  onChange={event =>
                    form.setFieldValue('quietHours.enabled', event.currentTarget.checked)
                  }
                />
                {form.values.quietHours.enabled && (
                  <Group gap={4} wrap="nowrap" align="center">
                    <TextInput
                      type="time"
                      aria-label="Quiet hours start"
                      {...form.getInputProps('quietHours.startTime')}
                      styles={timeInputStyles}
                    />
                    <Text size="sm" c="dimmed">
                      –
                    </Text>
                    <TextInput
                      type="time"
                      aria-label="Quiet hours end"
                      {...form.getInputProps('quietHours.endTime')}
                      styles={timeInputStyles}
                    />
                  </Group>
                )}
              </Group>
            </Group>

            <Divider my="xs" />

            <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
              <div>
                <Text fw={600}>Holiday hour rules</Text>
                <Text size="sm" c="dimmed">
                  Add only the holidays you need (closed or shortened). Dates are US holidays for {holidayLabelYear}.
                </Text>
              </div>
              <Button
                type="button"
                variant="light"
                size="xs"
                leftSection={<IconPlus size={14} />}
                disabled={viewOnly || holidayRuleRows.length >= HOLIDAY_IDS.length}
                onClick={addHolidayRow}
              >
                Add holiday
              </Button>
            </Group>

            {holidayRuleRows.length === 0 ? (
              <Text size="sm" c="dimmed">
                No holiday overrides. Regular weekly hours apply unless you add a holiday or use special hours below.
              </Text>
            ) : (
              holidayRuleRows.map((row, index) => {
                const isClosed = row.mode === 'closed'
                const usedOnOtherRows = new Set(
                  holidayRuleRows.map((r, i) => (i !== index ? r.holidayId : null)).filter(Boolean) as HolidayId[],
                )
                const holidaySelectData = HOLIDAY_IDS.filter(
                  id => !usedOnOtherRows.has(id) || id === row.holidayId,
                ).map(id => ({
                  value: id,
                  label: `${HOLIDAY_LABELS[id]} (${holidayDateLabels[id]})`,
                }))

                return (
                  <Group key={index} justify="space-between" align="center" wrap="wrap" gap="sm">
                    <Select
                      aria-label="Holiday"
                      data={holidaySelectData}
                      searchable
                      w={320}
                      styles={{ input: { flex: '1 1 200px' } }}
                      value={row.holidayId}
                      onChange={v => {
                        if (!v) {
                          return
                        }
                        const next = [...holidayRuleRows]
                        next[index] = { ...next[index], holidayId: v as HolidayId }
                        form.setFieldValue('holidayRuleRows', next)
                      }}
                    />
                    <Group gap="sm" wrap="wrap" justify="flex-end" align="center" style={{ flex: 1, minWidth: 200 }}>
                      <Checkbox
                        label="Closed"
                        checked={isClosed}
                        onChange={event => {
                          const next = [...holidayRuleRows]
                          if (event.currentTarget.checked) {
                            next[index] = { ...next[index], mode: 'closed' }
                          } else {
                            next[index] = {
                              ...next[index],
                              mode: 'shortened',
                              openTime: next[index].openTime ?? '10:00',
                              closeTime: next[index].closeTime ?? '16:00',
                            }
                          }
                          form.setFieldValue('holidayRuleRows', next)
                        }}
                      />
                      {!isClosed && (
                        <Group gap={4} wrap="nowrap">
                          <TextInput
                            type="time"
                            {...form.getInputProps(`holidayRuleRows.${index}.openTime`)}
                            styles={timeInputStyles}
                          />
                          <Text size="sm" c="dimmed">
                            –
                          </Text>
                          <TextInput
                            type="time"
                            {...form.getInputProps(`holidayRuleRows.${index}.closeTime`)}
                            styles={timeInputStyles}
                          />
                        </Group>
                      )}
                      <ActionIcon
                        disabled={viewOnly}
                        type="button"
                        variant="subtle"
                        color="red"
                        aria-label="Remove holiday rule"
                        onClick={() =>
                          form.setFieldValue(
                            'holidayRuleRows',
                            holidayRuleRows.filter((_, i) => i !== index),
                          )
                        }
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Group>
                )
              })
            )}

            <Divider my="xs" />

            <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
              <div>
                <Text fw={600}>Special hours</Text>
                <Text size="sm" c="dimmed">
                  One-off dates override holiday and regular hours for that department.
                </Text>
              </div>
              <Button
                disabled={viewOnly}
                type="button"
                variant="light"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => form.setFieldValue('specialHours', [...specialHours, emptySpecialHour()])}
              >
                Add date
              </Button>
            </Group>

            {specialHours.length === 0 ? (
              <Text size="sm" c="dimmed">
                No special hours configured.
              </Text>
            ) : (
              specialHours.map((_, index) => {
                const row = form.values.specialHours?.[index]
                const closed = row?.isClosed ?? false
                return (
                  <Group key={index} justify="space-between" align="center" wrap="wrap" gap="sm">
                    <TextInput
                      type="date"
                      aria-label="Special hours date"
                      styles={{ input: { width: 148 } }}
                      {...form.getInputProps(`specialHours.${index}.date`)}
                    />
                    <Select
                      aria-label="Department"
                      data={DEPARTMENT_OPTIONS}
                      w={130}
                      {...form.getInputProps(`specialHours.${index}.department`)}
                    />
                    {!closed && (
                      <>
                        <TextInput
                          type="time"
                          aria-label="Open"
                          {...form.getInputProps(`specialHours.${index}.openTime`)}
                          styles={timeInputStyles}
                        />
                        <Text size="sm" c="dimmed">
                          –
                        </Text>
                        <TextInput
                          type="time"
                          aria-label="Close"
                          {...form.getInputProps(`specialHours.${index}.closeTime`)}
                          styles={timeInputStyles}
                        />
                      </>
                    )}
                    <Switch
                      size="sm"
                      label={closed ? 'Closed' : 'Open'}
                      checked={!closed}
                      onChange={event =>
                        form.setFieldValue(`specialHours.${index}.isClosed`, !event.currentTarget.checked)
                      }
                    />
                    <TextInput
                      placeholder="Note"
                      aria-label="Note"
                      style={{ flex: '1 1 140px', minWidth: 100, maxWidth: 280 }}
                      {...form.getInputProps(`specialHours.${index}.description`)}
                    />
                    <ActionIcon
                      disabled={viewOnly}
                      type="button"
                      variant="subtle"
                      color="red"
                      aria-label="Remove special hours row"
                      onClick={() =>
                        form.setFieldValue(
                          'specialHours',
                          specialHours.filter((_, i) => i !== index),
                        )
                      }
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                )
              })
            )}
          </Stack>
        </Card>

      </Stack>
    </form>
  )
}
